import { useMemo, useState } from 'react';
import type { Dataset } from '../../../shared/types';
import {
  clampDate, comparePeriod, compareOverview, lastCompleteDay, OVERVIEW_COLS,
  type CompareResult, type GroupBy, type OverviewBlock,
} from '../../lib/compute';

const GROUPS: { id: GroupBy; label: string }[] = [
  { id: 'country', label: 'Land' },
  { id: 'day', label: 'Tag' },
  { id: 'week', label: 'KW' },
  { id: 'month', label: 'Monat' },
];

function addDays(date: string, n: number): string {
  const d = new Date(date + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

function PeriodTable({ data, label, from, to, groupBy, onFrom, onTo, result }: {
  data: Dataset; label: string; from: string; to: string; groupBy: GroupBy;
  onFrom: (v: string) => void; onTo: (v: string) => void; result: CompareResult;
}) {
  return (
    <section className="cw-card cw-cmp-card">
      <div className="cw-cmp-head">
        <span className="cw-cmp-title">{label}</span>
        <div className="cw-cmp-dates">
          <input type="date" className="cw-date" value={from} min={data.since} max={to} onChange={(e) => onFrom(clampDate(data, e.target.value))} />
          <span className="cw-date-sep">–</span>
          <input type="date" className="cw-date" value={to} min={from} max={data.until} onChange={(e) => onTo(clampDate(data, e.target.value))} />
        </div>
      </div>
      <div className="cw-table-scroll">
        <table className="cw-table">
          <thead>
            <tr>
              <th className="l">{groupBy === 'country' ? 'Land' : groupBy === 'day' ? 'Datum' : groupBy === 'week' ? 'KW' : 'Monat'}</th>
              <th className="r">Umsatz</th>
              <th className="r">Werbekosten</th>
              <th className="r">ROAS</th>
            </tr>
          </thead>
          <tbody>
            {result.rows.map((r) => (
              <tr key={r.label}>
                <td className="l kw">{r.label}</td>
                <td className="r">{r.umsatz}</td>
                <td className="r muted">{r.spend}</td>
                <td className="r roas">{r.roas}</td>
              </tr>
            ))}
            {result.rows.length === 0 && (
              <tr><td className="l" colSpan={4} style={{ color: '#A8A39D' }}>Keine Daten im Zeitraum.</td></tr>
            )}
          </tbody>
          <tfoot>
            <tr className="cw-cmp-total">
              <td className="l">Gesamt</td>
              <td className="r">{result.total.umsatz}</td>
              <td className="r">{result.total.spend}</td>
              <td className="r roas">{result.total.roas}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
}

const TONE_COLOR = { pos: '#2E9E6B', neg: '#CF4B3E', neutral: '#8a8f94' } as const;

function OverviewCard({ block }: { block: OverviewBlock }) {
  const cellVals = (c: NonNullable<OverviewBlock['cur']>) => [c.roas, c.umsatz, c.spend, c.orders, c.aov];
  const row = (label: string, caption: string, cells: OverviewBlock['cur'], strong: boolean) => (
    <tr>
      <td className={`l${strong ? ' kw' : ''}`}>
        {label}
        <span className="cw-ovw-cap">{caption}</span>
      </td>
      {cells
        ? cellVals(cells).map((v, i) => (
            <td key={i} className={`r${i === 0 ? ' roas' : i === 2 ? ' muted' : ''}`}>{v}</td>
          ))
        : OVERVIEW_COLS.map((_, i) => <td key={i} className="r muted">—</td>)}
    </tr>
  );
  return (
    <section className="cw-card cw-ovw-card">
      <div className="cw-ovw-head">
        <span className="cw-cmp-title">{block.title}</span>
      </div>
      <div className="cw-table-scroll">
        <table className="cw-table cw-ovw-table">
          <thead>
            <tr>
              <th className="l">Zeitraum</th>
              {OVERVIEW_COLS.map((c) => <th key={c} className="r">{c}</th>)}
            </tr>
          </thead>
          <tbody>
            {row(block.curLabel, block.curCaption, block.cur, true)}
            {row(block.benchLabel, block.benchCaption, block.bench, false)}
            <tr className="cw-ovw-delta">
              <td className="l">Δ Delta</td>
              {block.deltas.map((dl, i) =>
                dl ? (
                  <td key={i} className="r cw-num" style={{ color: TONE_COLOR[dl.tone] }}>
                    {dl.abs}
                    <span className="pct">{dl.pct}</span>
                  </td>
                ) : (
                  <td key={i} className="r muted">—</td>
                ),
              )}
            </tr>
          </tbody>
        </table>
      </div>
      {block.note && <div className="cw-ovw-note">{block.note}</div>}
    </section>
  );
}

export function Vergleich({ data, selCountries, selChannels }: { data: Dataset; selCountries: string[]; selChannels: string[] }) {
  const [groupBy, setGroupBy] = useState<GroupBy>('country');
  const channelsFiltered = selChannels.length < data.channels.length;

  // Performance-Überblick: all five comparison blocks derive from this anchor
  // day (default: yesterday = the last complete day).
  const lastComplete = useMemo(() => lastCompleteDay(data), [data]);

  // Defaults: B = last 30 COMPLETE days (same window as the "30 Tage" pill —
  // ending on data.until would include the partial current day and silently
  // drop the first day vs. the Cockpit), A = the 30 days before that.
  const bTo = lastComplete;
  const bFrom = clampDate(data, addDays(bTo, -29));
  const aTo = clampDate(data, addDays(bFrom, -1));
  const aFrom = clampDate(data, addDays(aTo, -29));

  const [a, setA] = useState({ from: aFrom, to: aTo });
  const [b, setB] = useState({ from: bFrom, to: bTo });
  const [anchor, setAnchor] = useState(lastComplete);
  const overview = useMemo(
    () => compareOverview(data, selCountries, selChannels, anchor),
    [data, selCountries, selChannels, anchor],
  );

  const resA = useMemo(() => comparePeriod(data, a.from, a.to, groupBy, selCountries, selChannels), [data, a, groupBy, selCountries, selChannels]);
  const resB = useMemo(() => comparePeriod(data, b.from, b.to, groupBy, selCountries, selChannels), [data, b, groupBy, selCountries, selChannels]);

  const dRev = resB.total.umsatzNum - resA.total.umsatzNum;
  const dRevPct = resA.total.umsatzNum ? (dRev / resA.total.umsatzNum) * 100 : 0;

  return (
    <>
      <div className="cw-pagehead">
        <h1>Zeitraum-Vergleich</h1>
        <span className="meta">Performance-Überblick &amp; freier A/B-Vergleich — folgt der Kanal- und Länderauswahl</span>
      </div>

      <div className="cw-ovw-toolbar">
        <span className="cw-filter-label">Performance-Überblick</span>
        <span className="cw-ovw-hint">Stichtag</span>
        <input
          type="date" className="cw-date" value={anchor} min={data.since} max={lastComplete}
          onChange={(e) => setAnchor(clampDate(data, e.target.value || lastComplete))}
        />
        {anchor !== lastComplete && (
          <button className="cw-range-btn" onClick={() => setAnchor(lastComplete)}>Zurück zu Gestern</button>
        )}
      </div>

      <div className="cw-ovw-grid">
        {overview.map((blk) => <OverviewCard key={blk.title} block={blk} />)}
      </div>

      <div className="cw-sectionhead">Freier A/B-Vergleich</div>

      <div className="cw-cmp-toolbar">
        <span className="cw-filter-label">Aufschlüsselung</span>
        <div className="cw-rangebar">
          {GROUPS.map((g) => (
            <button key={g.id} className={`cw-range-btn${g.id === groupBy ? ' is-active' : ''}`} onClick={() => setGroupBy(g.id)}>
              {g.label}
            </button>
          ))}
        </div>
        <div className="cw-spacer" />
        <span className="cw-cmp-delta cw-num" style={{ color: dRev >= 0 ? '#2E9E6B' : '#CF4B3E' }}>
          {dRev >= 0 ? '▲ +' : '▼ −'}{Math.abs(dRevPct).toFixed(1).replace('.', ',')} % Umsatz (B vs. A)
        </span>
      </div>

      <div className="cw-cmp-grid">
        <PeriodTable data={data} label="Zeitraum A" from={a.from} to={a.to} groupBy={groupBy}
          onFrom={(v) => setA((s) => ({ ...s, from: v }))} onTo={(v) => setA((s) => ({ ...s, to: v }))} result={resA} />
        <PeriodTable data={data} label="Zeitraum B" from={b.from} to={b.to} groupBy={groupBy}
          onFrom={(v) => setB((s) => ({ ...s, from: v }))} onTo={(v) => setB((s) => ({ ...s, to: v }))} result={resB} />
      </div>

      <div className="cw-sources">
        ROAS ist länderbasiert (Umsatz ÷ Werbekosten je Land).{' '}
        {channelsFiltered ? 'Kanal-Auswahl aktiv: alle Spalten folgen den gewählten Kanälen (Werbekosten nach Kanal-Attribution: Amazon Ads → Amazon, OTTO Ads → OTTO, Meta/Google → Webshops). ' : ''}
        {data.amazonAdsSpendPending ? 'Amazon-Ads-Kosten noch nicht enthalten.' : ''} · {data.sources}
      </div>
    </>
  );
}
